import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, APIError } from '../../core/_axios';
import { Account } from '../../core/_models';
import { managementGetAccounts } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import ExportActivityReportModal from './ExportActivityReportModal';
import {
    ColumnDef,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    flexRender,
} from '@tanstack/react-table';

/** Remove punctuation & spaces, fold to lowercase. */
const normalize = (s: string): string =>
    s.toLowerCase().replace(/[^a-z0-9]/g, '');   // “T.S. Warden” → “tswarden”

export function AccountsList() {
    const axios = useAxios();
    usePageTitle('View Accounts');

    /* ------------------------------------------------------------------ */
    /* state                                                               */
    /* ------------------------------------------------------------------ */
    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [totalAccounts, setTotalAccounts] = useState<number | null>(null);

    /* ── “Hide demo accounts” checkbox ────────────────────────────────── */
    const localStorageKeyToHideDemoAccounts = 'accountsList-hideDemoAccounts';
    const [hideDemoAccounts, setHideDemoAccounts] = useState<boolean>(false);

    /* ── text search box ──────────────────────────────────────────────── */
    const [searchTerm, setSearchTerm] = useState<string>('');

    /* ── Export Report (activity per month, counted by the backend) ───── */
    const [exportReportOpen, setExportReportOpen] = useState<boolean>(false);

    /* ------------------------------------------------------------------ */
    /* initialisation                                                      */
    /* ------------------------------------------------------------------ */
    /* 1. Load persisted setting for demo filter */
    useEffect(() => {
        const stored = localStorage.getItem(localStorageKeyToHideDemoAccounts);
        if (stored !== null) setHideDemoAccounts(stored === 'true');
    }, []);

    /* 2. Load accounts from API */
    const loadAccounts = useCallback(async () => {
        setLoading(true);
        setLoadingError(null);
        try {
            const { accounts: a, total_accounts: t } = await managementGetAccounts(axios);
            setAccounts(a);
            setTotalAccounts(t);
        } catch (e) {
            console.error(e);
            setLoadingError(
                e instanceof APIError ? e.message : 'An unknown error has occurred. Please try again.',
            );
        } finally {
            setLoading(false);
        }
    }, [axios]);

    useEffect(() => void loadAccounts(), [loadAccounts]);

    /* ------------------------------------------------------------------ */
    /* derived / filtered data                                             */
    /* ------------------------------------------------------------------ */
    const filteredAccounts = useMemo(() => {
        let list = accounts;

        /* Hide demo accounts if requested.  _How_ you detect a demo account
           may differ in your schema—adjust the predicate as needed.            */
        if (hideDemoAccounts) list = list.filter((a: Account) => !a.is_demo_account); // or a.private_data?.is_demo

        /* Text search across a few obvious fields */
        if (searchTerm.trim() !== '') {
            const q = normalize(searchTerm);

            list = list.filter(a => {
                const fields = [
                    a.name ?? '',
                    typeof a.belongs_to_agency === 'string'
                        ? a.belongs_to_agency
                        : a.belongs_to_agency?.name ?? '',
                    a.unique_identifier,
                ];

                return fields.some(f => normalize(f).includes(q));
            });
        }

        return list;
    }, [accounts, hideDemoAccounts, searchTerm]);

    /* ------------------------------------------------------------------ */
    /* CSV (honour filters)                                                */
    /* ------------------------------------------------------------------ */
    const onDownloadCurrentListToCSV = useCallback(() => {
        if (!filteredAccounts.length) return;

        const rows = filteredAccounts.map(a => ({
            Name: a.name ?? '',
            Agency:
                typeof a.belongs_to_agency === 'string'
                    ? a.belongs_to_agency
                    : a.belongs_to_agency?.name ?? '',
            'Last Service': a.last_service_at
                ? DateTime.fromISO(a.last_service_at).toFormat('yyyy-MM-dd HH:mm:ss')
                : '',
            'Last Signature': a.last_signature_at
                ? DateTime.fromISO(a.last_signature_at).toFormat('yyyy-MM-dd HH:mm:ss')
                : '',
            Created: a.inserted_at
                ? DateTime.fromISO(a.inserted_at).toFormat('yyyy-MM-dd HH:mm:ss')
                : '',
            'Unique Identifier': a.unique_identifier,
            'Demo Account': a.is_demo_account ? 'Yes' : 'No',
        }));

        const headers = Object.keys(rows[0]) as (keyof typeof rows[0])[];
        const escapeCSV = (v: string) => `"${v.replace(/"/g, '""')}"`;
        const csv =
            headers.join(',') +
            '\n' +
            rows.map(r => headers.map(h => escapeCSV(r[h] ?? '')).join(',')).join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'accounts.csv';
        a.click();
        URL.revokeObjectURL(url);
    }, [filteredAccounts]);

    /* ------------------------------------------------------------------ */
    /* react-table                                                         */
    /* ------------------------------------------------------------------ */
    const columns = useMemo<ColumnDef<Account>[]>(() => {
        return [
            {
                accessorKey: 'name',
                header: 'Name',
                cell: ({ getValue, row }) => (
                    <Link
                        className="text-blue-600 hover:underline"
                        to={`/management/accounts/${row.original.unique_identifier}`}
                    >
                        {getValue() as string}
                    </Link>
                ),
            },
            {
                accessorKey: 'belongs_to_agency',
                header: 'Agency',
                cell: ({ getValue }) => {
                    const v = getValue();
                    if (typeof v === 'string') return v;
                    if (v && typeof v === 'object' && 'name' in v) return (v as any).name;
                    return '';
                },
                sortingFn: (a, b, id) => {
                    const getName = (v: any) =>
                        typeof v === 'string' ? v : v?.name ?? '';
                    return getName(a.getValue(id)).localeCompare(getName(b.getValue(id)));
                },
            },
            {
                accessorKey: 'last_service_at',
                header: 'Last Service',
                cell: ({ getValue }) => DateTime.fromISO(getValue() as string).toRelative(),
            },
            {
                accessorKey: 'last_signature_at',
                header: 'Last Signature',
                cell: ({ getValue }) => DateTime.fromISO(getValue() as string).toRelative(),
            },
            {
                accessorKey: 'inserted_at',
                header: 'Created',
                cell: ({ getValue }) => DateTime.fromISO(getValue() as string).toRelative(),
            },
        ];
    }, []);

    const table = useReactTable({
        data: filteredAccounts,          // <── use filtered data here
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    /* ------------------------------------------------------------------ */
    /* rendering                                                           */
    /* ------------------------------------------------------------------ */
    if (loading)
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );

    if (loadingError)
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                    {loadingError}
                </div>
                <button className="btn btn-lg btn-light mt-5" onClick={loadAccounts}>
                    Retry
                </button>
            </div>
        );

    return (
        <div className="container-fixed">
            <div className="card card-grid min-w-full">
                <div className="card-header flex-wrap py-3">
                    {/* ── Search box - wider and more prominent ────────────────────────────── */}
                    <div className="relative flex-1 max-w-md">
                        <i className="ki-filled ki-magnifier absolute top-1/2 left-0 -translate-y-1/2 ml-3 text-md text-gray-500" />
                        <input
                            className="input input-sm pl-8 w-full"
                            placeholder={totalAccounts ? `Search ${totalAccounts.toLocaleString()} accounts...` : 'Search accounts...'}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex gap-6 items-center">

                        {/* ── Hide-demo switch ──────────────────────── */}
                        <label className="switch switch-sm">
                            <input
                                className="order-2"
                                type="checkbox"
                                checked={hideDemoAccounts}
                                onChange={() => {
                                    const next = !hideDemoAccounts;
                                    setHideDemoAccounts(next);
                                    next
                                        ? localStorage.setItem(localStorageKeyToHideDemoAccounts, 'true')
                                        : localStorage.removeItem(localStorageKeyToHideDemoAccounts);
                                }}
                            />
                            <span className="switch-label order-1">Hide Demo Accounts</span>
                        </label>

                        {/* ── Activity report, per month ────────────── */}
                        <button className="btn btn-sm btn-light" onClick={() => setExportReportOpen(true)}>
                            Export Report
                        </button>

                        {/* ── CSV download ──────────────────────────── */}
                        <button className="btn btn-sm btn-light" onClick={onDownloadCurrentListToCSV}>
                            Download to CSV
                        </button>
                    </div>
                </div>

                <ExportActivityReportModal isOpen={exportReportOpen} setIsOpen={setExportReportOpen} />

                <div className="card-body overflow-x-auto">
                    <table className="table table-fixed table-border">
                        <thead>
                            {table.getHeaderGroups().map(hg => (
                                <tr key={hg.id}>
                                    {hg.headers.map(h => (
                                        <th
                                            key={h.id}
                                            className="px-4 py-2 bg-gray-100 cursor-pointer"
                                            onClick={h.column.getToggleSortingHandler()}
                                        >
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                            {h.column.getIsSorted() === 'asc'
                                                ? ' 🔼'
                                                : h.column.getIsSorted() === 'desc'
                                                    ? ' 🔽'
                                                    : ''}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.length ? (
                                table.getRowModel().rows.map(r => (
                                    <tr key={r.id} className="border-t">
                                        {r.getVisibleCells().map(c => (
                                            <td key={c.id} className="px-4 py-2">
                                                {flexRender(c.column.columnDef.cell, c.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center py-4">
                                        No accounts match your filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
